// LazyLoad.tsx
import React, { useEffect, useRef, useState } from "react";

type ResolveContainerResult = Window | HTMLElement;

/** Resolve container: HTMLElement | selector string | null -> HTMLElement | Window */
function resolveContainer(container?: HTMLElement | string | null): ResolveContainerResult {
  if (typeof window === "undefined" || typeof document === "undefined") return ({} as Window);
  if (!container) return window;
  if (typeof container === "string") {
    const el = document.querySelector(container);
    return (el as HTMLElement) || window;
  }
  return container;
}

/** Hook: detect "scrolling" and whether user has scrolled at least once. */
function useScrollStop(container?: HTMLElement | string | null, delay = 200) {
  const [isScrolling, setIsScrolling] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const el: any = resolveContainer(container);
    const onScroll = () => {
      setHasScrolled(true);
      setIsScrolling(true);
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      timeoutRef.current = window.setTimeout(() => setIsScrolling(false), delay);
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [container, delay]);

  return { isScrolling, hasScrolled };
}

type LazyLoadProps = {
  children: React.ReactNode;
  placeholder?: React.ReactNode;
  /** milliseconds to wait after last scroll to consider "stopped" */
  delay?: number;
  /** HTMLElement | selector string ('.foo' / '#bar') | undefined (defaults to window) */
  container?: HTMLElement | string | null;
  /** IntersectionObserver options */
  rootMargin?: string;
  threshold?: number | number[];
  /** default false: wait for first scroll stop before loading visible items */
  loadOnMount?: boolean;
  /** stop observing after loaded */
  once?: boolean;
};

export const LazyLoad: React.FC<LazyLoadProps> = ({
  children,
  placeholder = <div style={{ minHeight: 200, background: "#eee" }} />,
  delay = 200,
  container,
  rootMargin = "0px",
  threshold = 0,
  loadOnMount = true,
  once = true,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [isIntersecting, setIsIntersecting] = useState(false);

  const { isScrolling, hasScrolled } = useScrollStop(container, delay);

  // Observe intersection with container (or window)
  useEffect(() => {
    if (typeof window === "undefined") {
      setIsIntersecting(true); // SSR fallback
      return;
    }
    const node = wrapperRef.current;
    if (!node) return;

    const rootCandidate = resolveContainer(container);
    const observer = "IntersectionObserver" in window
      ? new IntersectionObserver(
          (entries) => {
            const e = entries[0];
            setIsIntersecting(Boolean(e?.isIntersecting));
          },
          {
            root: rootCandidate === window ? null : (rootCandidate as HTMLElement),
            rootMargin,
            threshold,
          }
        )
      : null;

    if (observer) observer.observe(node);
    else setIsIntersecting(true); // fallback if IO isn't available

    return () => {
      if (observer && node) {
        observer.unobserve(node);
        observer.disconnect();
      }
    };
  }, [container, rootMargin, threshold]);

  // Decide when to load:
  // - element must be intersecting
  // - scrolling must be stopped
  // - if loadOnMount=false, user must have scrolled at least once (prevents "load all on first mount")
  useEffect(() => {
    if (loaded) return;
    if (!isIntersecting) return;
    if (isScrolling) return;
    if (!loadOnMount && !hasScrolled) return;

    setLoaded(true);
  }, [isIntersecting, isScrolling, hasScrolled, loadOnMount, loaded]);

  // optional: if once=true we can stop observing (the effect above already stops further loads since `loaded` prevents re-running)
  return (
    <div ref={wrapperRef} data-lazyloaded={loaded ? "1" : "0"}>
      {loaded ? children : placeholder}
    </div>
  );
};

export default LazyLoad;
